package com.vamvisions.main.portfolio.presentation.portfolio

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.vamvisions.main.core.domain.Result
import com.vamvisions.main.core.presentation.i18n.Strings
import com.vamvisions.main.core.presentation.i18n.stringsFor
import com.vamvisions.main.core.presentation.i18n.toUiText
import com.vamvisions.main.portfolio.domain.model.Experience
import com.vamvisions.main.portfolio.domain.model.ExperienceSection
import com.vamvisions.main.portfolio.domain.repository.ExperienceRepository
import com.vamvisions.main.portfolio.presentation.common.LanguageLoader
import com.vamvisions.main.portfolio.presentation.common.PeriodFormatter
import com.vamvisions.main.portfolio.presentation.common.linkTargetOf
import com.vamvisions.main.portfolio.presentation.common.toSkillUi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/** Loads the experience entries once the page reports its language, and again on every change. */
class PortfolioViewModel(
    private val experienceRepository: ExperienceRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(PortfolioState())
    val state = _state.asStateFlow()

    private val loader = LanguageLoader(
        scope = viewModelScope,
        fetch = experienceRepository::get,
        onStart = { _state.update { it.copy(isLoading = true, error = null) } },
        onResult = { language, result ->
            _state.update {
                when (result) {
                    is Result.Success -> result.data.toState(stringsFor(language))
                    is Result.Error -> PortfolioState(isLoading = false, error = result.error.toUiText())
                }
            }
        },
    )

    fun onAction(action: PortfolioAction) {
        when (action) {
            PortfolioAction.Retry -> loader.retry()
            is PortfolioAction.LanguageChanged -> loader.languageChanged(action.language)
        }
    }

    private fun List<Experience>.toState(strings: Strings): PortfolioState {
        val bySection = groupBy { it.section }
        fun entries(section: ExperienceSection) = bySection[section].orEmpty().map { it.toExperienceUi(strings) }
        // The first timeline entry is the current one while it is ongoing.
        val timeline = bySection[ExperienceSection.TIMELINE].orEmpty().mapIndexed { index, experience ->
            experience.toExperienceUi(strings, isCurrent = index == 0 && experience.end == null)
        }
        return PortfolioState(
            isLoading = false,
            timeline = timeline,
            learning = entries(ExperienceSection.LEARNING),
            education = entries(ExperienceSection.EDUCATION),
        )
    }

    private fun Experience.toExperienceUi(strings: Strings, isCurrent: Boolean = false) = ExperienceUi(
        slug = slug,
        name = name,
        role = role,
        period = PeriodFormatter.format(start, end, strings),
        highlights = highlights,
        link = url?.let(::linkTargetOf),
        skills = skills.map { it.toSkillUi() },
        isCurrent = isCurrent,
    )
}
